import { formatDashboardCategoryLane } from "@/data/product-categories";
import type { DoOrDumpDerived } from "@/lib/do-or-dump-compute";
import { highImpactBodyFromDashboardPayload, highImpactBodyFromFunnel, type DoOrDumpHighImpactBody } from "@/lib/do-or-dump-high-impact-body";
import type { DoOrDumpVerdict } from "@/lib/do-or-dump-compute";
import type { DoOrDumpDashboardPayload } from "@/types/do-or-dump-dashboard";
import type { ProductPrimarySelection } from "@/data/product-categories";

/** Full snapshot for report narrative — sent to the model and POST /api/do-or-dump/report-narrative. */
export type DoOrDumpReportNarrativeSnapshot = DoOrDumpHighImpactBody & {
  categoryLane: string;
  productPrimary: string;
  productSub: string;
};

export function reportNarrativeSnapshotFromDashboardPayload(p: DoOrDumpDashboardPayload): DoOrDumpReportNarrativeSnapshot {
  return {
    ...highImpactBodyFromDashboardPayload(p),
    categoryLane: formatDashboardCategoryLane(p.productPrimary, p.productSub),
    productPrimary: p.productPrimary,
    productSub: p.productSub,
  };
}

function reqStr(o: Record<string, unknown>, k: string): string {
  const v = o[k];
  if (typeof v !== "string") throw new Error(`Invalid or missing field: ${k}`);
  return v;
}

function reqNum(o: Record<string, unknown>, k: string): number {
  const v = o[k];
  if (typeof v !== "number" || !Number.isFinite(v)) throw new Error(`Invalid or missing field: ${k}`);
  return v;
}

function optStr(o: Record<string, unknown>, k: string): string | undefined {
  const v = o[k];
  if (v === undefined) return undefined;
  if (typeof v !== "string") throw new Error(`Invalid field: ${k}`);
  return v;
}

/** Validate POST body from the client — throws if malformed. */
export function assertReportNarrativeSnapshotBody(raw: unknown): DoOrDumpReportNarrativeSnapshot {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) {
    throw new Error("Invalid JSON body.");
  }
  const o = raw as Record<string, unknown>;
  const verdict = o.verdict;
  if (verdict !== "DO" && verdict !== "DUMP") {
    throw new Error("Invalid verdict.");
  }
  let unitsForGoal: number | null;
  if (o.unitsForGoal === null) unitsForGoal = null;
  else if (typeof o.unitsForGoal === "number" && Number.isFinite(o.unitsForGoal)) unitsForGoal = o.unitsForGoal;
  else throw new Error("Invalid unitsForGoal.");

  return {
    categoryPath: reqStr(o, "categoryPath"),
    categoryLane: reqStr(o, "categoryLane"),
    productPrimary: reqStr(o, "productPrimary"),
    productSub: reqStr(o, "productSub"),
    productDifferentiation: reqStr(o, "productDifferentiation"),
    whyYou: reqStr(o, "whyYou"),
    verdict: verdict as DoOrDumpVerdict,
    verdictLine: reqStr(o, "verdictLine"),
    momentum: reqNum(o, "momentum"),
    capacity: reqNum(o, "capacity"),
    unitsForGoal,
    goalMonthly: reqNum(o, "goalMonthly"),
    profitPerItem: reqNum(o, "profitPerItem"),
    monthlyProfitGuess: reqNum(o, "monthlyProfitGuess"),
    ordersPerMonth: reqNum(o, "ordersPerMonth"),
    price: reqNum(o, "price"),
    cost: reqNum(o, "cost"),
    followers: reqNum(o, "followers"),
    avgViews: reqNum(o, "avgViews"),
    acquisitionsLabel: reqStr(o, "acquisitionsLabel"),
    timeTier: reqStr(o, "timeTier"),
    audienceTier: reqStr(o, "audienceTier"),
    acquisitionTier: reqStr(o, "acquisitionTier"),
    hoursPerWeek: reqNum(o, "hoursPerWeek"),
    investmentBudgetUsd: reqNum(o, "investmentBudgetUsd"),
    cashTimelineLabel: optStr(o, "cashTimelineLabel"),
  };
}

export function reportNarrativeSnapshotFromFunnel(args: {
  categoryPath: string;
  productPrimary: ProductPrimarySelection;
  productSub: string;
  productDifferentiation: string;
  whyYou: string;
  derived: DoOrDumpDerived;
  ordersPerMonth: number;
  price: number;
  cost: number;
  followers: number;
  avgViews: number;
  acquisitionsLabel: string;
  goalMonthly: number;
  hoursPerWeek: number;
  investmentBudgetUsd: number;
  cashTimelineLabel?: string | null;
}): DoOrDumpReportNarrativeSnapshot {
  const base = highImpactBodyFromFunnel({
    categoryPath: args.categoryPath,
    productDifferentiation: args.productDifferentiation,
    whyYou: args.whyYou,
    derived: args.derived,
    ordersPerMonth: args.ordersPerMonth,
    price: args.price,
    cost: args.cost,
    followers: args.followers,
    avgViews: args.avgViews,
    acquisitionsLabel: args.acquisitionsLabel,
    goalMonthly: args.goalMonthly,
    hoursPerWeek: args.hoursPerWeek,
    investmentBudgetUsd: args.investmentBudgetUsd,
    cashTimelineLabel: args.cashTimelineLabel ?? undefined,
  });
  const primaryStr = typeof args.productPrimary === "string" ? args.productPrimary : "";
  return {
    ...base,
    categoryLane: formatDashboardCategoryLane(primaryStr, args.productSub),
    productPrimary: primaryStr,
    productSub: args.productSub,
  };
}
